/** Right-side drawer with every field of one External access row: who called (key, partner,
 *  OAuth user, tenant), where (channel, operation, client), how it ended, the tracing ids,
 *  the sizes and the redacted argument summary as formatted JSON.
 *
 *  There is no detail endpoint — the list row carries every column — so the drawer renders
 *  the row it was handed. Request and response bodies are never in the access log: they
 *  stay in the linked API request / import run. */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import type { ExternalAccessItem } from '@/api/externalAccess';
import { useSnackbar } from '@/components/Snackbar';
import { CopyableId, ValueActions, act, utcTitle } from '@/components/ValueActions/ValueActions';
import { StatusCodeChip, formatMs } from '@/features/api-requests/apiRequestFormat';
import { formatBytes } from '@/utils/formatBytes';
import { formatDate } from '@/utils/format';

import { ChannelChip, OutcomeChip, PRINCIPAL_LABELS } from './externalAccessFormat';
import {
  apiRequestActions,
  credentialActions,
  importRunActions,
  ipActions,
  partnerActions,
  requestIdActions,
  tenantActions,
  timeActions,
  userActions,
} from './externalAccessLinks';

const mono = { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' } as const;

interface Props {
  row: ExternalAccessItem | null;
  onClose: () => void;
}

export function ExternalAccessDetailDrawer({ row, onClose }: Props) {
  return (
    <Drawer
      anchor="right"
      open={row !== null}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1; without this it covers the drawer's top.
      sx={{ zIndex: (t) => t.zIndex.modal }}
    >
      <Box sx={{ width: { xs: '100vw', sm: 520 }, maxWidth: '100vw', p: 2 }} role="presentation">
        <Stack direction="row" alignItems="center" sx={{ mb: 2, gap: 1 }}>
          <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
            External call {row && <CopyableId value={row.id} label="Copy row id" />}
          </Typography>
          {row && <OutcomeChip outcome={row.outcome} />}
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
        {row && <CallDetails row={row} />}
      </Box>
    </Drawer>
  );
}

function CallDetails({ row }: { row: ExternalAccessItem }) {
  return (
    <Stack spacing={1.5}>
      <Grid>
        <Field label="Time">
          <ValueActions title={utcTitle(row.created_ts)} actions={timeActions(row.created_ts)}>
            {formatDate(row.created_ts)}
          </ValueActions>
        </Field>
        <Field label="Channel">
          <ChannelChip channel={row.channel} />
        </Field>
        <Field label="Status">{row.status ? <StatusCodeChip code={row.status} /> : '—'}</Field>
        <Field label="Method">{row.method ? <Box component="span" sx={mono}>{row.method}</Box> : '—'}</Field>
        <Field label="Duration">{formatMs(row.duration_ms ?? null)}</Field>
        <Field label="Error code">
          {row.error_code ? (
            <ValueActions actions={[act.copy(row.error_code, 'Copy error code')]}>
              <Box component="span" sx={{ ...mono, color: 'error.main' }}>{row.error_code}</Box>
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
      </Grid>
      <Field label="Operation">
        <ValueActions actions={[act.copy(row.operation, 'Copy operation')]}>
          <Box component="span" sx={mono}>{row.operation}</Box>
        </ValueActions>
      </Field>

      <Divider />
      <Typography variant="subtitle2">Who</Typography>
      <Grid>
        <Field label="Principal">
          {row.principal_kind ? PRINCIPAL_LABELS[row.principal_kind] : '—'}
        </Field>
        <Field label="API key">
          {row.credential_id != null ? (
            <ValueActions actions={credentialActions(row.credential_id, row.credential)}>
              {row.credential ? (
                <>
                  {row.credential.name}
                  {row.credential.key_prefix && (
                    <Box component="span" sx={{ ...mono, color: 'text.secondary' }}>
                      {' '}
                      {row.credential.key_prefix}…
                    </Box>
                  )}
                </>
              ) : (
                <Gone what={`Key #${row.credential_id}`} />
              )}
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
        {row.credential && (
          <Field label="Key scope">
            <Box component="span" sx={mono}>{row.credential.scope}</Box>
          </Field>
        )}
        <Field label="Partner">
          {row.partner_id != null ? (
            <ValueActions actions={partnerActions(row.partner_id, row.partner)}>
              {row.partner ? `${row.partner.name} (${row.partner.code})` : <Gone what={`Partner #${row.partner_id}`} />}
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
        <Field label="OAuth user">
          {row.user_id != null ? (
            <ValueActions actions={userActions(row.user_id, row.user)}>
              {row.user ? row.user.email : <Gone what={`User #${row.user_id}`} />}
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Tenant">
          {row.tenant_id != null ? (
            <ValueActions actions={tenantActions(row.tenant_id, row.tenant)}>
              {row.tenant ? `${row.tenant.name} (${row.tenant.code})` : <Gone what={`Tenant #${row.tenant_id}`} />}
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
        <Field label="OAuth client">{text(row.oauth_client_id)}</Field>
        <Field label="OAuth grant">{text(row.grant_id)}</Field>
        <Field label="MCP client">{text(row.mcp_client)}</Field>
        <Field label="MCP session">{text(row.mcp_session_id)}</Field>
      </Grid>

      <Divider />
      <Typography variant="subtitle2">Request</Typography>
      <Grid>
        <Field label="Request ID">
          {row.request_id ? (
            <ValueActions actions={requestIdActions(row.request_id)}>
              <Box component="span" sx={mono}>{row.request_id}</Box>
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
        <Field label="IP">
          {row.ip ? (
            <ValueActions actions={ipActions(row.ip)}>
              <Box component="span" sx={mono}>{row.ip}</Box>
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Request size">{bytes(row.request_bytes)}</Field>
        <Field label="Response size">{bytes(row.response_bytes)}</Field>
        <Field label="API request">
          {row.api_request_id != null ? (
            <ValueActions actions={apiRequestActions(row.api_request_id)}>
              <Box component="span" sx={mono}>#{row.api_request_id}</Box>
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Import run">
          {row.import_run_id ? (
            <ValueActions actions={importRunActions(row.import_run_id)}>
              <Box component="span" sx={mono}>{row.import_run_id}</Box>
            </ValueActions>
          ) : (
            '—'
          )}
        </Field>
      </Grid>
      <Field label="User agent">{text(row.user_agent)}</Field>

      <Divider />
      <Typography variant="subtitle2">Arguments</Typography>
      <Typography variant="caption" color="text.secondary">
        A redacted, cut-down summary: values with secret-sounding names are masked, long strings
        and lists are shortened. Bodies are never copied here.
      </Typography>
      <Field label="Digest">
        {row.args_digest ? (
          <ValueActions actions={[act.copy(row.args_digest, 'Copy digest')]}>
            <Box component="span" sx={mono}>{row.args_digest}</Box>
          </ValueActions>
        ) : (
          '—'
        )}
      </Field>
      {row.args_summary != null ? (
        <JsonBlock value={row.args_summary} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No argument summary recorded.
        </Typography>
      )}
    </Stack>
  );
}

const text = (v: string | null | undefined) => (v ? <Box component="span" sx={mono}>{v}</Box> : '—');
const bytes = (n: number | null | undefined) => (n == null ? '—' : formatBytes(n));

/** A record the row names that no longer exists (rows outlive keys, users, partners). */
function Gone({ what }: { what: string }) {
  return (
    <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
      {what} (no longer exists)
    </Box>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
      {children}
    </Box>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" component="div">
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ overflowWrap: 'anywhere' }}>
        {children}
      </Typography>
    </Box>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  const snackbar = useSnackbar();
  const json = JSON.stringify(value, null, 2);
  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title="Copy JSON">
        <IconButton
          size="small"
          aria-label="Copy JSON"
          sx={{ position: 'absolute', top: 4, right: 4 }}
          onClick={() =>
            void navigator.clipboard?.writeText(json).then(
              () => snackbar.show('Copied.', 'success'),
              () => snackbar.show('Copy failed — clipboard unavailable', 'error'),
            )
          }
        >
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        aria-label="Argument summary"
        sx={{
          m: 0,
          p: 1,
          pr: 4,
          bgcolor: 'action.hover',
          borderRadius: 1,
          fontSize: 12,
          fontFamily: 'monospace',
          overflow: 'auto',
          maxHeight: 320,
        }}
      >
        {json}
      </Box>
    </Box>
  );
}
